/**
 * DateRangeSelector Component
 * 
 * Provides preset date range options and custom date picker for filtering dashboard data.
 */

import React, { useState, useRef, useEffect } from 'react';
import styles from './DateRangeSelector.module.scss';

export type DateRangePreset = '5m' | '15m' | '30m' | '1h' | '6h' | '24h' | '7d' | '30d' | '90d' | 'all' | 'custom';

export interface DateRange {
  preset: DateRangePreset;
  startDate: string | null; // ISO datetime string
  endDate: string | null;   // ISO datetime string
}

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const PRESET_OPTIONS: { value: DateRangePreset; label: string; group?: string }[] = [
  { value: '5m', label: 'Last 5 minutes', group: 'Minutes' },
  { value: '15m', label: 'Last 15 minutes', group: 'Minutes' },
  { value: '30m', label: 'Last 30 minutes', group: 'Minutes' },
  { value: '1h', label: 'Last hour', group: 'Hours' },
  { value: '6h', label: 'Last 6 hours', group: 'Hours' },
  { value: '24h', label: 'Last 24 hours', group: 'Hours' },
  { value: '7d', label: 'Last 7 days', group: 'Days' },
  { value: '30d', label: 'Last 30 days', group: 'Days' },
  { value: '90d', label: 'Last 90 days', group: 'Days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

/**
 * Get display label for current selection
 */
function getDisplayLabel(range: DateRange): string {
  if (range.preset === 'custom' && range.startDate && range.endDate) {
    const start = new Date(range.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = new Date(range.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${start} - ${end}`;
  }
  return PRESET_OPTIONS.find(o => o.value === range.preset)?.label || 'Select range';
}

/**
 * Calculate date range from preset
 * Returns full ISO datetime strings for precise filtering
 */
export function getDateRangeFromPreset(preset: DateRangePreset): { startDate: string | null; endDate: string | null } {
  const now = new Date();
  const endDate = now.toISOString();
  
  switch (preset) {
    case '5m': {
      const start = new Date(now);
      start.setMinutes(start.getMinutes() - 5);
      return { startDate: start.toISOString(), endDate };
    }
    case '15m': {
      const start = new Date(now);
      start.setMinutes(start.getMinutes() - 15);
      return { startDate: start.toISOString(), endDate };
    }
    case '30m': {
      const start = new Date(now);
      start.setMinutes(start.getMinutes() - 30);
      return { startDate: start.toISOString(), endDate };
    }
    case '1h': {
      const start = new Date(now);
      start.setHours(start.getHours() - 1);
      return { startDate: start.toISOString(), endDate };
    }
    case '6h': {
      const start = new Date(now);
      start.setHours(start.getHours() - 6);
      return { startDate: start.toISOString(), endDate };
    }
    case '24h': {
      const start = new Date(now);
      start.setHours(start.getHours() - 24);
      return { startDate: start.toISOString(), endDate };
    }
    case '7d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: start.toISOString(), endDate };
    }
    case '30d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString(), endDate };
    }
    case '90d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      return { startDate: start.toISOString(), endDate };
    }
    case 'all':
      return { startDate: null, endDate: null };
    case 'custom':
      return { startDate: null, endDate: null };
    default:
      return { startDate: null, endDate: null };
  }
}

// Group preset options by their group property
const GROUPED_PRESETS = PRESET_OPTIONS.reduce((acc, option) => {
  const group = option.group || 'Other';
  if (!acc[group]) {
    acc[group] = [];
  }
  acc[group]!.push(option);
  return acc;
}, {} as Record<string, typeof PRESET_OPTIONS>);

const GROUP_ORDER = ['Minutes', 'Hours', 'Days', 'Other'];

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStart, setCustomStart] = useState(value.startDate || '');
  const [customEnd, setCustomEnd] = useState(value.endDate || '');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetSelect = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setShowCustomPicker(true);
    } else {
      const { startDate, endDate } = getDateRangeFromPreset(preset);
      onChange({ preset, startDate, endDate });
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onChange({
        preset: 'custom',
        startDate: customStart,
        endDate: customEnd,
      });
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button 
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className={styles.icon}>📅</span>
        <span className={styles.label}>{getDisplayLabel(value)}</span>
        <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {!showCustomPicker ? (
            <div className={styles.presetList}>
              {GROUP_ORDER.map(groupName => {
                const options = GROUPED_PRESETS[groupName];
                if (!options || options.length === 0) return null;
                
                return (
                  <div key={groupName} className={styles.presetGroup}>
                    {groupName !== 'Other' && (
                      <div className={styles.presetGroupLabel}>{groupName}</div>
                    )}
                    {options.map(option => (
                      <button
                        key={option.value}
                        className={`${styles.presetOption} ${value.preset === option.value ? styles.active : ''}`}
                        onClick={() => handlePresetSelect(option.value)}
                        type="button"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.customPicker}>
              <div className={styles.customHeader}>
                <button 
                  className={styles.backButton}
                  onClick={() => setShowCustomPicker(false)}
                  type="button"
                >
                  ← Back
                </button>
                <span>Custom Range</span>
              </div>
              <div className={styles.dateInputs}>
                <label>
                  <span>Start Date</span>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    max={customEnd || undefined}
                  />
                </label>
                <label>
                  <span>End Date</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    min={customStart || undefined}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </label>
              </div>
              <button
                className={styles.applyButton}
                onClick={handleCustomApply}
                disabled={!customStart || !customEnd}
                type="button"
              >
                Apply Range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;

